import React, { FC, useState, useMemo, useEffect, useCallback } from 'react'
import { Modal } from 'react-bootstrap'
import { KTIcon } from '../../../_payairo/helpers'
import {PageTitle, useLayout} from '../../../_payairo/layout/core'
import Swal from 'sweetalert2'
import axios from 'axios'
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  Column,
} from 'react-table'
import clsx from 'clsx'

const ACCENT = '#064e3b' // Forest Green
const BORDER = '#d1fae5' // Light emerald border

// Define Asset type based on API
type Asset = {
  id: number
  module: string
  name: string
  image: string | null
  image_url: string | null
  description_primary: string
  description_secondary: string
  is_active: boolean
  created_at: string
  updated_at: string
}

const ManagedModuleAssets: FC = () => {
  const [assets, setAssets] = useState<Asset[]>([])
  const [showModal, setShowModal] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(false)
  
  const [moduleFilter, setModuleFilter] = useState('All')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('All') // All, true, false

  // Image Upload States
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  const fetchAssets = useCallback(async (silent = false) => {
    if (!silent) setFetching(true)
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || 'https://stagingfastapi.payairo.com'
      const token = localStorage.getItem('token')
      
      const params: any = {}
      if (moduleFilter !== 'All') params.module = moduleFilter.toLowerCase()
      if (statusFilter !== 'All') params.is_active = statusFilter === 'true'
      if (searchTerm) params.q = searchTerm

      const response = await axios.get(`${baseUrl}/api/v1/users/admin/managed-module-assets/`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.data.ok) {
        // Flatten the nested structure: data.modules -> items
        const rawModules = response.data.data.modules || []
        const flattenedAssets = rawModules.reduce((acc: Asset[], mod: any) => {
          return [...acc, ...(mod.items || [])]
        }, [])
        setAssets(flattenedAssets)
      }
    } catch (err: any) {
      console.error('Error fetching assets:', err)
    } finally {
      if (!silent) setFetching(false)
    }
  }, [moduleFilter, statusFilter, searchTerm])

  useEffect(() => {
    fetchAssets()
  }, [fetchAssets])

  // Columns definition
  const columns: Column<Asset>[] = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'id',
        Cell: ({ value }) => <span className='text-gray-400 fw-bold fs-8 pe-4 font-monospace'>#{value}</span>,
      },
      {
        Header: 'NAME',
        accessor: 'name',
        Cell: ({ row, value }: any) => (
          <div className='d-flex align-items-center gap-3'>
            <div className='symbol symbol-35px symbol-circle flex-shrink-0 border border-gray-100 shadow-sm'>
              {row.original.image_url ? (
                <img src={row.original.image_url} alt='' className='object-fit-cover' />
              ) : (
                <div className='symbol-label fs-8 fw-boldest' style={{ backgroundColor: BORDER, color: ACCENT }}>
                  {value[0]}
                </div>
              )}
            </div>
            <span className='text-gray-900 fw-boldest fs-6 tracking-tight'>{value}</span>
          </div>
        ),
      },
      {
        Header: 'MODULE',
        accessor: 'module',
        Cell: ({ value }) => (
          <span
            className='badge fw-bolder fs-9 px-3 py-2 rounded-1 text-uppercase tracking-widest'
            style={{ backgroundColor: BORDER, color: ACCENT }}
          >
            {value}
          </span>
        ),
      },
      {
        Header: 'PRIMARY DESCRIPTION',
        accessor: 'description_primary',
        Cell: ({ value }) => <div className='max-w-xs truncate text-gray-700 fw-medium fs-7'>{value || '—'}</div>,
      },
      {
        Header: 'STATUS',
        accessor: 'is_active',
        Cell: ({ value }) => (
          <div className='d-flex align-items-center'>
            <span
              className={`w-8px h-8px rounded-circle me-3 ${
                value ? 'bg-success ripple-active' : 'bg-danger'
              }`}
            ></span>
            <span
              className={`fw-bolder fs-8 text-uppercase tracking-wide ${
                value ? 'text-success' : 'text-danger'
              }`}
            >
              {value ? 'Active' : 'Inactive'}
            </span>
          </div>
        ),
      },
      {
        Header: 'ACTIONS',
        id: 'actions',
        Cell: ({ row }: any) => (
          <div className='d-flex align-items-center gap-4'>
            <button
              onClick={() => handleEdit(row.original)}
              className='btn btn-sm btn-light-emerald btn-hover-emerald w-80px rounded-3 border-0 fw-bolder fs-8 transition-all'
              style={{ backgroundColor: BORDER, color: ACCENT }}
            >
              EDIT
            </button>
            <div className='form-check form-switch form-check-custom form-check-solid'>
              <input
                className='form-check-input h-20px w-35px cursor-pointer border-0 shadow-none'
                type='checkbox'
                checked={row.original.is_active}
                onChange={() => handleToggleStatus(row.original)}
              />
            </div>
          </div>
        ),
      },
    ],
    []
  )

  const table = useTable(
    { columns, data: assets },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  const [isEdit, setIsEdit] = useState(false)

  const handleCreate = () => {
    setSelectedAsset({
      name: '',
      module: 'home',
      description_primary: '',
      description_secondary: '',
      is_active: true,
    })
    setIsEdit(false)
    setImageFile(null)
    setImagePreview(null)
    setShowModal(true)
  }

  const handleEdit = async (asset: Asset) => {
    setSelectedAsset(null) // Reset
    setIsEdit(true)
    setImageFile(null)
    setImagePreview(null)
    setShowModal(true)
    setLoading(true)
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || 'https://stagingfastapi.payairo.com'
      const token = localStorage.getItem('token')
      const response = await axios.get(`${baseUrl}/api/v1/users/admin/managed-module-assets/${asset.id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.data.ok) {
        setSelectedAsset(response.data.data.asset)
      } else {
        Swal.fire('Error', response.data.message || 'Failed to fetch asset details', 'error')
        setShowModal(false)
      }
    } catch (err: any) {
      console.error('Error fetching asset details:', err)
      Swal.fire('Error', 'Failed to fetch asset details from API', 'error')
      setShowModal(false)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleStatus = async (asset: Asset) => {
    const newStatus = !asset.is_active
    const result = await Swal.fire({
      title: 'Update Status?',
      text: `Confirm setting "${asset.name}" to ${newStatus ? 'Active' : 'Inactive'}.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, change it!',
      confirmButtonColor: ACCENT,
      cancelButtonColor: '#f3f4f6',
      customClass: {
        confirmButton: 'rounded-lg font-bold px-6',
        cancelButton: 'rounded-lg font-bold px-6 text-gray-600',
      },
    })

    if (result.isConfirmed) {
      try {
        const baseUrl = process.env.REACT_APP_BASE_URL || 'https://stagingfastapi.payairo.com'
        const token = localStorage.getItem('token')
        const response = await axios.patch(
          `${baseUrl}/api/v1/users/admin/managed-module-assets/${asset.id}/`,
          {is_active: newStatus},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        if (response.data.ok) {
          setAssets((prev) =>
            prev.map((a) => (a.id === asset.id ? {...a, is_active: newStatus} : a))
          )
          Swal.fire({
            icon: 'success',
            title: 'Updated!',
            text: 'Status changed successfully.',
            timer: 1500,
            showConfirmButton: false,
          })
        } else {
          Swal.fire('Error', response.data.message || 'Failed to update status', 'error')
        }
      } catch (err: any) {
        console.error('Error updating status:', err)
        Swal.fire('Error', 'An error occurred while updating status', 'error')
      }
    }
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (imagePreview) URL.revokeObjectURL(imagePreview)
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || 'https://stagingfastapi.payairo.com'
      const token = localStorage.getItem('token')
      
      const formData = new FormData()
      formData.append('name', selectedAsset.name)
      formData.append('module', selectedAsset.module)
      formData.append('description_primary', selectedAsset.description_primary)
      formData.append('description_secondary', selectedAsset.description_secondary || '')
      formData.append('is_active', String(selectedAsset.is_active))
      
      if (imageFile) {
        formData.append('image', imageFile)
      }

      const url = isEdit 
        ? `${baseUrl}/api/v1/users/admin/managed-module-assets/${selectedAsset.id}/`
        : `${baseUrl}/api/v1/users/admin/managed-module-assets/`
      
      const response = await (isEdit 
        ? axios.patch(url, formData, { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            } 
          })
        : axios.post(url, formData, { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            } 
          })
      )

      if (response.data.ok) {
        fetchAssets(true) // Refresh list
        setShowModal(false)
        if (imagePreview) URL.revokeObjectURL(imagePreview)
        setImageFile(null)
        setImagePreview(null)
        Swal.fire({
          icon: 'success',
          title: `Asset ${isEdit ? 'Updated' : 'Created'}`,
          text: `Changes have been saved successfully.`,
          timer: 1500,
          showConfirmButton: false,
        })
      } else {
        if (response.data.message?.includes('unique set')) {
          Swal.fire('Unique Set Error', 'The combination of Module and Name must be unique. An asset with this name already exists in this module.', 'error')
        } else {
          Swal.fire('Error', response.data.message || 'Failed to save changes', 'error')
        }
      }
    } catch (err: any) {
      console.error('Error saving asset:', err)
      const errorMsg = err.response?.data?.message || ''
      if (errorMsg.includes('unique set')) {
         Swal.fire('Validation Error', 'The fields module, name must make a unique set.', 'error')
      } else {
         Swal.fire('Error', err.response?.data?.message || 'An error occurred while saving changes', 'error')
      }
    } finally {
      setLoading(false)
    }
  }

  const {setRightTolbar} = useLayout()

  useEffect(() => {
    setRightTolbar('')
    return () => setRightTolbar('')
  }, [setRightTolbar])

  return (
    <div id='tailwind-root' className='font-inter animate-fade-in'>
      <PageTitle>Managed Module Assets</PageTitle>

      <div className='max-w-[1440px] mx-auto p-6 md:p-10'>
        {/* Header Section - Matches Acces managment */}
        <div className='d-flex flex-wrap justify-content-between align-items-start gap-4 mb-8'>
          <div>
            <h1
              className='fw-bolder fs-1 text-gray-900 mb-2'
              style={{ color: ACCENT, letterSpacing: '-0.02em' }}
            >
              Managed Module Assets
            </h1>
            <p className='text-gray-600 fs-6 mb-0 fw-medium'>
              Manage and control app feature tiles effortlessly.
            </p>
          </div>
          <div className='d-flex gap-4 align-items-center'>
            <button
               onClick={handleCreate}
               className='btn btn-primary d-flex align-items-center gap-2 px-6 py-3 rounded-3 fw-boldest shadow-sm'
               style={{ backgroundColor: ACCENT, borderColor: ACCENT }}
            >
               <i className='fas fa-plus fs-6' />
               ADD NEW ASSET
            </button>
            <div className='d-flex gap-3 align-items-center bg-white p-3 px-6 rounded-4 shadow-sm border border-gray-100'>
              <div className='symbol symbol-40px flex-shrink-0'>
                <div className='symbol-label rounded-3' style={{ backgroundColor: BORDER }}>
                  <i className='fas fa-th-large fs-4' style={{ color: ACCENT }} />
                </div>
              </div>
              <div>
                <div className='text-gray-500 fs-9 fw-bolder text-uppercase tracking-widest'>
                  Total Assets
                </div>
                <div className='fs-4 fw-bolder' style={{ color: ACCENT }}>
                  {assets?.length}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter & Search Bar - Enhanced prominence */}
        <div className='card border-0 shadow-sm rounded-4 mb-8 bg-white'>
          <div className='card-body py-6 px-10'>
            <div className='row g-5 align-items-center'>
              <div className='col-lg-8'>
                <div className='position-relative'>
                  <i className='fas fa-search position-absolute top-50 translate-middle-y ms-6 text-gray-400 fs-5' />
                  <input
                    type='text'
                    className='form-control form-control-solid ps-15 py-4 rounded-3 border-0 bg-light fs-6 fw-bold'
                    placeholder='Search assets by name or description...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className='col-lg-4'>
                <div className='position-relative'>
                  <i className='fas fa-filter position-absolute top-50 translate-middle-y ms-6 text-emerald fs-5' style={{ color: ACCENT }} />
                  <select
                    className='form-select form-select-solid ps-15 py-4 rounded-3 border-0 bg-light fs-6 fw-bold cursor-pointer'
                    value={moduleFilter}
                    onChange={(e) => setModuleFilter(e.target.value)}
                  >
                    <option value='All'>All Modules</option>
                    <option value='home'>Home</option>
                    <option value='wallet'>Wallet</option>
                    <option value='crypto'>Crypto</option>
                    <option value='settings'>Settings</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table Section - Aligned with AllUsers */}
        <div className='card border-0 shadow-sm rounded-4 bg-white overflow-hidden'>
          <div className='card-body p-0'>
            <div className='table-responsive'>
              <table
                {...table.getTableProps()}
                className='table table-row-bordered table-row-gray-200 align-middle gs-10 gy-6 mb-0'
              >
                <thead className='bg-light bg-opacity-30 border-bottom border-gray-200'>
                  <tr className='text-gray-500 fw-bolder fs-9 tracking-widest text-uppercase'>
                    {table.headerGroups.map((headerGroup) =>
                      headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          className='py-5'
                          key={column.id}
                        >
                          <div className='d-flex align-items-center cursor-pointer text-hover-emerald' style={{ transition: 'all 0.2s' }}>
                            {column.render('Header')}
                            {column.isSorted ? (
                              <i className={`fas fa-sort-amount-${column.isSortedDesc ? 'down' : 'up'} ms-2 fs-9 text-emerald`} style={{ color: ACCENT }} />
                            ) : (
                              <i className='fas fa-sort ms-2 fs-9 text-gray-300' />
                            )}
                          </div>
                        </th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody {...table.getTableBodyProps()} className='fw-bold'>
                  {table.page.length > 0 ? (
                    table.page.map((row) => {
                      table.prepareRow(row)
                      return (
                        <tr
                          {...row.getRowProps()}
                          className='hover-elevated-sm transition-all border-bottom border-gray-100'
                          key={row.id}
                        >
                          {row.cells.map((cell) => (
                            <td {...cell.getCellProps()} className='py-6' key={cell.column.id}>
                              {cell.render('Cell')}
                            </td>
                          ))}
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={columns.length} className='text-center py-20 bg-light bg-opacity-20'>
                        <i className='fas fa-search-minus fs-2x mb-4 text-gray-300' />
                        <h4 className='fw-bold text-gray-400 fs-5'>No matching assets found.</h4>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination - Matching AllUsers */}
            <div className='d-flex flex-stack justify-content-between flex-wrap py-8 px-10 bg-light bg-opacity-10 border-top border-gray-200'>
              <div className='fs-8 fw-bolder text-gray-500 text-uppercase tracking-wider'>
                 Showing <span className='text-gray-900 fw-bolder'>{table.state.pageIndex * table.state.pageSize + 1}</span> —{' '}
                 <span className='text-gray-900 fw-bolder'>{Math.min((table.state.pageIndex + 1) * table.state.pageSize, assets.length)}</span> of <span className='text-gray-900 fw-bolder'>{assets.length}</span> entries
              </div>
              <div className='d-flex gap-2'>
                <button
                  className='btn btn-sm btn-icon btn-light rounded-circle shadow-sm'
                  disabled={!table.canPreviousPage}
                  onClick={() => table.previousPage()}
                >
                  <i className='fas fa-chevron-left text-gray-600' />
                </button>
                <div className='d-flex gap-1'>
                  {Array.from({ length: table.pageCount }, (_, i) => i).map((page) => (
                  <button
                    key={page}
                    className={`btn btn-sm btn-icon rounded-circle transition-all ${
                      table.state.pageIndex === page
                        ? 'btn-primary shadow-sm'
                        : 'btn-light text-gray-600'
                    }`}
                    style={{
                      backgroundColor: table.state.pageIndex === page ? ACCENT : undefined,
                      borderColor: table.state.pageIndex === page ? ACCENT : undefined,
                    }}
                    onClick={() => table.gotoPage(page)}
                  >
                    <span className='fw-bolder fs-8'>{page + 1}</span>
                  </button>
                  ))}
                </div>
                <button
                  className='btn btn-sm btn-icon btn-light rounded-circle shadow-sm'
                  disabled={!table.canNextPage}
                  onClick={() => table.nextPage()}
                >
                  <i className='fas fa-chevron-right text-gray-600' />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal - Restored and themed */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        className='modal-user-profile'
        contentClassName='rounded-4 border-0 shadow-lg overflow-hidden font-inter'
      >
        <div className='modal-content border-0'>
          <div className='modal-header border-0 pb-2 pt-8 px-8 bg-white d-flex align-items-center justify-content-between'>
            <div>
              <h2 className='fw-bolder fs-1 mb-1' style={{ color: ACCENT, letterSpacing: '-0.02em' }}>
                {isEdit ? 'Edit Module Asset' : 'Add New Module Asset'}
              </h2>
              <p className='text-gray-500 fs-7 mb-0 fw-medium'>
                {isEdit ? 'Update feature details and control app visibility' : 'Create a new feature tile for the app'}
              </p>
            </div>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-light-emerald rounded-circle shadow-sm'
              style={{ backgroundColor: BORDER }}
              onClick={() => setShowModal(false)}
            >
              <i className='fas fa-times fs-4' style={{ color: ACCENT }} />
            </button>
          </div>

          <div className='modal-body px-8 pt-6 pb-2 scroll-y' style={{ maxHeight: '70vh' }}>
            {!selectedAsset ? (
              <div className='d-flex flex-column align-items-center py-20 text-gray-400'>
                <span
                  className='spinner-border spinner-border-sm mb-4'
                  role='status'
                  style={{color: ACCENT}}
                />
                <span className='fw-bolder fs-8 tracking-widest text-uppercase'>
                  Fetching Details...
                </span>
              </div>
            ) : (
              <form onSubmit={handleSave} className='space-y-8'>
                 {/* Image Upload Section */}
                 <div className='d-flex flex-center flex-column mb-10'>
                    <div className='symbol symbol-100px symbol-circle mb-5 shadow-sm position-relative overflow-hidden' style={{ border: `2px dashed ${BORDER}` }}>
                        {imagePreview || selectedAsset.image_url ? (
                            <img src={imagePreview || selectedAsset.image_url} alt='icon' className='w-100 h-100 object-fit-cover rounded-circle' />
                        ) : (
                            <div className='symbol-label fs-1 fw-boldest' style={{ backgroundColor: BORDER + '40', color: ACCENT }}>
                                {selectedAsset.name?.[0] || 'A'}
                            </div>
                        )}
                        <label className='btn btn-icon btn-circle btn-active-color-primary w-30px h-30px bg-white shadow-sm position-absolute border border-gray-100' style={{ bottom: '2px', right: '2px', cursor: 'pointer' }}>
                            <i className='fas fa-camera fs-8 text-gray-600'></i>
                            <input type='file' accept='image/*' hidden onChange={handleImageChange} />
                        </label>
                    </div>
                    <div className='text-gray-500 fs-9 fw-bolder text-uppercase tracking-widest'>Click to upload asset icon</div>
                 </div>

                 <div className='row g-10 mb-8'>
                    <div className='col-md-6'>
                       <label className='text-gray-500 fw-bolder text-uppercase tracking-widest fs-9 mb-3 d-block'>Asset Name</label>
                       <input
                        type='text'
                        className='form-control form-control-solid fw-bold border-0 bg-light py-4 rounded-3'
                        value={selectedAsset.name}
                        onChange={(e) => setSelectedAsset({ ...selectedAsset, name: e.target.value })}
                        placeholder='e.g. Add Money'
                        required
                      />
                    </div>
                    <div className='col-md-6'>
                       <label className='text-gray-500 fw-bolder text-uppercase tracking-widest fs-9 mb-3 d-block'>
                        Module {isEdit && <span className='lowercase opacity-50'>(readonly)</span>}
                       </label>
                       {isEdit ? (
                         <input
                          type='text'
                          className='form-control form-control-solid fw-bold border-0 bg-light-emerald py-4 rounded-3 text-emerald cursor-not-allowed'
                          style={{ backgroundColor: BORDER, color: ACCENT }}
                          value={selectedAsset.module}
                          readOnly
                        />
                       ) : (
                         <select
                          className='form-select form-select-solid fw-bold border-0 bg-light py-4 rounded-3 cursor-pointer'
                          value={selectedAsset.module}
                          onChange={(e) => setSelectedAsset({ ...selectedAsset, module: e.target.value })}
                          required
                         >
                           <option value='home'>Home</option>
                           <option value='wallet'>Wallet</option>
                           <option value='crypto'>Crypto</option>
                           <option value='settings'>Settings</option>
                         </select>
                       )}
                    </div>
                 </div>

                 <div className='mb-8'>
                    <label className='text-gray-500 fw-bolder text-uppercase tracking-widest fs-9 mb-3 d-block'>Primary Description</label>
                    <input
                      type='text'
                      className='form-control form-control-solid fw-bold border-0 bg-light py-4 rounded-3'
                      value={selectedAsset.description_primary}
                      onChange={(e) => setSelectedAsset({ ...selectedAsset, description_primary: e.target.value })}
                      required
                    />
                 </div>

                 <div className='mb-8'>
                    <label className='text-gray-500 fw-bolder text-uppercase tracking-widest fs-9 mb-3 d-block'>Secondary Description</label>
                    <textarea
                      rows={3}
                      className='form-control form-control-solid fw-bold border-0 bg-light py-4 rounded-3 resize-none'
                      value={selectedAsset.description_secondary}
                      onChange={(e) => setSelectedAsset({ ...selectedAsset, description_secondary: e.target.value })}
                    />
                 </div>

                 <div className='p-6 rounded-4 d-flex align-items-center justify-content-between border border-emerald-outline' style={{ backgroundColor: BORDER + '40', border: `1px solid ${BORDER}` }}>
                    <div>
                        <div className='fw-boldest fs-6 mb-1' style={{ color: ACCENT }}>Asset Status</div>
                        <div className='text-gray-500 fs-8 fw-medium'>Enable or disable this feature on the app</div>
                    </div>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input h-25px w-50px cursor-pointer border-0 shadow-none'
                        type='checkbox'
                        checked={selectedAsset.is_active}
                        onChange={(e) =>
                          setSelectedAsset({
                            ...selectedAsset,
                            is_active: e.target.checked,
                          })
                        }
                      />
                    </div>
                 </div>

                 <div className='modal-footer border-0 bg-white pt-10 px-0 pb-8 d-flex gap-4'>
                    <button
                      type='button'
                      className='btn btn-light flex-grow-1 px-8 py-4 rounded-3 fw-bolder fs-7'
                      onClick={() => setShowModal(false)}
                    >
                      CANCEL
                    </button>
                    <button
                      type='submit'
                      className='btn btn-primary flex-grow-1 px-8 py-4 rounded-3 fw-bolder fs-7 shadow-sm'
                      style={{ backgroundColor: ACCENT, borderColor: ACCENT }}
                      disabled={loading}
                    >
                      {loading ? (
                        <span className='spinner-border spinner-border-sm' />
                      ) : (
                        <>
                          <i className='fas fa-check-circle fs-6 me-2' />
                          SAVE CHANGES
                        </>
                      )}
                    </button>
                 </div>
              </form>
            )}
          </div>
        </div>
      </Modal>

      <style>{`
        .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

        .ripple-active { animation: ripple 2s infinite linear; }
        @keyframes ripple { 0% { box-shadow: 0 0 0 0 rgba(71, 190, 125, 0.2); } 70% { box-shadow: 0 0 0 10px rgba(71, 190, 125, 0); } 100% { box-shadow: 0 0 0 0 rgba(71, 190, 125, 0); } }

        .hover-elevated-sm:hover {
           background-color: #fcfcfd !important;
           transform: translateY(-1px);
           box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.035) !important;
        }

        .btn-hover-emerald:hover {
           background-color: ${ACCENT} !important;
           color: #fff !important;
        }

        #tailwind-root .form-switch .form-check-input:checked {
           background-color: ${ACCENT} !important;
           border-color: ${ACCENT} !important;
        }

        .text-hover-emerald:hover { color: ${ACCENT} !important; }
      `}</style>
    </div>
  )
}

export default ManagedModuleAssets
